/*
 * Platin collection infinite scroll
 * Drop-in replacement for the legacy remote Ajaxinate build previously loaded
 * by theme.liquid. Keeps the existing Ajaxinate constructor API used by theme.js
 * while making page loading deterministic and duplicate-safe.
 */
(function () {
  'use strict';

  function normalizeUrl(rawUrl) {
    try {
      var url = new URL(rawUrl, window.location.href);
      url.hash = '';
      url.searchParams.delete('section_id');
      url.searchParams.delete('_platin_retry');
      return url.toString();
    } catch (error) {
      return String(rawUrl || '');
    }
  }

  function productHandleSet(root) {
    var handles = new Set();
    if (!root || !root.querySelectorAll) return handles;

    root.querySelectorAll('product-card[handle]').forEach(function (card) {
      var handle = (card.getAttribute('handle') || '').trim();
      if (handle) handles.add(handle);
    });

    return handles;
  }

  function Ajaxinate(config) {
    var settings = config || {};

    this.settings = {
      container: settings.container || '#AjaxinateContainer',
      pagination: settings.pagination || '#AjaxinatePagination',
      method: settings.method || 'scroll',
      offset: typeof settings.offset === 'number' ? settings.offset : 0,
      loadingText: settings.loadingText || 'Loading',
      callback: typeof settings.callback === 'function' ? settings.callback : null
    };

    this.containerElement = null;
    this.paginationElement = null;
    this.nextPageLinkElement = null;
    this.nextPageUrl = '';
    this.loading = false;
    this.destroyed = false;
    this.loadedUrls = new Set();
    this.abortController = null;
    this.originalLoadingText = '';
    this.paginationObserver = null;

    this.checkIfPaginationInView = this.checkIfPaginationInView.bind(this);
    this.stopMultipleClicks = this.stopMultipleClicks.bind(this);

    this.initialize();
  }

  Ajaxinate.prototype.initialize = function initialize() {
    if (this.destroyed) return;

    this.containerElement = document.querySelector(this.settings.container);
    this.paginationElement = document.querySelector(this.settings.pagination);

    if (!this.containerElement || !this.paginationElement) {
      this.destroy();
      return;
    }

    this.nextPageLinkElement = this.paginationElement.querySelector('[data-platin-next-page][href], a[href]');

    if (!this.nextPageLinkElement) {
      this.removePaginationElement();
      return;
    }

    // Always intercept the visible fallback link. Even in scroll mode a missed
    // observer/scroll event must never turn "Loading More" into a full-page
    // navigation.
    this.addClickListener();

    if (this.settings.method === 'click') return;

    this.addScrollListener();
    this.addPaginationObserver();
    window.requestAnimationFrame(this.checkIfPaginationInView);
  };

  Ajaxinate.prototype.addScrollListener = function addScrollListener() {
    this.removeScrollListener();
    document.addEventListener('scroll', this.checkIfPaginationInView, { passive: true });
    window.addEventListener('scroll', this.checkIfPaginationInView, { passive: true });
    window.addEventListener('resize', this.checkIfPaginationInView, { passive: true });
    window.addEventListener('orientationchange', this.checkIfPaginationInView, { passive: true });
  };

  Ajaxinate.prototype.removeScrollListener = function removeScrollListener() {
    document.removeEventListener('scroll', this.checkIfPaginationInView);
    window.removeEventListener('scroll', this.checkIfPaginationInView);
    window.removeEventListener('resize', this.checkIfPaginationInView);
    window.removeEventListener('orientationchange', this.checkIfPaginationInView);
  };

  Ajaxinate.prototype.addPaginationObserver = function addPaginationObserver() {
    this.removePaginationObserver();

    if (!this.paginationElement || typeof IntersectionObserver === 'undefined') return;

    var self = this;
    var margin = Math.max(0, Number(this.settings.offset) || 0);

    this.paginationObserver = new IntersectionObserver(function (entries) {
      if (self.destroyed || self.loading) return;

      for (var i = 0; i < entries.length; i += 1) {
        if (entries[i].isIntersecting) {
          self.checkIfPaginationInView();
          break;
        }
      }
    }, {
      root: null,
      rootMargin: margin + 'px 0px ' + margin + 'px 0px',
      threshold: 0
    });

    this.paginationObserver.observe(this.paginationElement);
  };

  Ajaxinate.prototype.removePaginationObserver = function removePaginationObserver() {
    if (this.paginationObserver) {
      try {
        this.paginationObserver.disconnect();
      } catch (error) {}
      this.paginationObserver = null;
    }
  };

  Ajaxinate.prototype.addClickListener = function addClickListener() {
    this.removeClickListener();
    if (this.nextPageLinkElement) {
      this.nextPageLinkElement.addEventListener('click', this.stopMultipleClicks);
    }
  };

  Ajaxinate.prototype.removeClickListener = function removeClickListener() {
    if (this.nextPageLinkElement) {
      this.nextPageLinkElement.removeEventListener('click', this.stopMultipleClicks);
    }
  };

  Ajaxinate.prototype.stopMultipleClicks = function stopMultipleClicks(event) {
    event.preventDefault();
    if (this.loading || !this.nextPageLinkElement) return;
    this.loadMore(this.nextPageLinkElement.href);
  };

  Ajaxinate.prototype.checkIfPaginationInView = function checkIfPaginationInView() {
    if (this.destroyed || this.loading || !this.paginationElement) return;

    var link = this.paginationElement.querySelector('[data-platin-next-page][href], a[href]');
    if (!link) {
      this.removePaginationElement();
      return;
    }

    this.nextPageLinkElement = link;

    var rect = this.paginationElement.getBoundingClientRect();
    var offset = Math.max(0, Number(this.settings.offset) || 0);

    // Match the legacy Ajaxinate semantics: once the pagination approaches the
    // viewport from below, load. Do not require it to still be visible; mobile
    // browsers can coalesce scroll events and skip directly past a tiny
    // pagination element.
    if (rect.top <= window.innerHeight + offset) {
      this.loadMore(link.href);
    }
  };

  Ajaxinate.prototype.requestDocument = async function requestDocument(pageUrl, sectionOnly) {
    var requestUrl = new URL(pageUrl, window.location.href);

    if (sectionOnly) {
      var sectionId = this.containerElement && this.containerElement.getAttribute('data-platin-section-id');
      if (sectionId) requestUrl.searchParams.set('section_id', sectionId);
    }

    var response = await fetch(requestUrl.toString(), {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      },
      signal: this.abortController ? this.abortController.signal : undefined
    });

    if (!response.ok) {
      throw new Error('Infinite scroll request failed with HTTP ' + response.status);
    }

    var html = await response.text();
    return new DOMParser().parseFromString(html, 'text/html');
  };

  Ajaxinate.prototype.getNextDocument = async function getNextDocument(pageUrl) {
    var sectionId = this.containerElement && this.containerElement.getAttribute('data-platin-section-id');

    if (sectionId) {
      var sectionDocument = await this.requestDocument(pageUrl, true);
      if (sectionDocument.querySelector(this.settings.container)) {
        return sectionDocument;
      }
    }

    return this.requestDocument(pageUrl, false);
  };

  Ajaxinate.prototype.prepareIncomingContainer = function prepareIncomingContainer(newContainer) {
    var workingContainer = newContainer.cloneNode(true);
    var existingHandles = productHandleSet(this.containerElement);
    var incomingCards = Array.prototype.slice.call(workingContainer.querySelectorAll('product-card[handle]'));
    var uniqueCount = 0;

    incomingCards.forEach(function (card) {
      var handle = (card.getAttribute('handle') || '').trim();

      if (handle && existingHandles.has(handle)) {
        card.remove();
        return;
      }

      if (handle) existingHandles.add(handle);
      uniqueCount += 1;
    });

    return {
      container: workingContainer,
      sourceCardCount: incomingCards.length,
      uniqueCardCount: uniqueCount
    };
  };

  Ajaxinate.prototype.loadMore = async function loadMore(rawUrl) {
    if (this.destroyed || this.loading || !rawUrl) return;

    var canonicalUrl = normalizeUrl(rawUrl);
    if (!canonicalUrl) return;

    // A next-page URL may never be consumed twice by one instance. If the
    // pagination returned by Shopify points backwards, stop rather than append
    // the same collection page forever.
    if (this.loadedUrls.has(canonicalUrl)) {
      console.warn('Platin infinite scroll stopped a repeated page URL:', canonicalUrl);
      this.removePaginationElement();
      return;
    }

    this.loading = true;
    this.nextPageUrl = canonicalUrl;
    this.loadedUrls.add(canonicalUrl);
    this.removeScrollListener();
    this.removePaginationObserver();
    this.removeClickListener();

    if (this.nextPageLinkElement) {
      this.originalLoadingText = this.nextPageLinkElement.textContent || '';
      this.nextPageLinkElement.textContent = this.settings.loadingText;
      this.nextPageLinkElement.setAttribute('aria-busy', 'true');
    }

    this.abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;

    try {
      var responseDocument = await this.getNextDocument(canonicalUrl);
      if (this.destroyed) return;

      var newContainer = responseDocument.querySelector(this.settings.container);
      var newPagination = responseDocument.querySelector(this.settings.pagination);

      if (!newContainer) {
        throw new Error('Infinite scroll response is missing ' + this.settings.container);
      }

      var prepared = this.prepareIncomingContainer(newContainer);

      // Shopify collections cannot legitimately contain the exact same product
      // handles as the already rendered page. Treat that as a bad/repeated
      // response and fail closed instead of showing duplicates to shoppers.
      if (prepared.sourceCardCount > 0 && prepared.uniqueCardCount === 0) {
        console.warn('Platin infinite scroll stopped a duplicate collection response:', canonicalUrl);
        this.removePaginationElement();
        return;
      }

      this.containerElement.insertAdjacentHTML('beforeend', prepared.container.innerHTML);

      if (newPagination) {
        this.paginationElement.innerHTML = newPagination.innerHTML;
      } else {
        this.paginationElement.innerHTML = '';
      }

      var returnedNextLink = this.paginationElement.querySelector('[data-platin-next-page][href], a[href]');
      if (returnedNextLink && this.loadedUrls.has(normalizeUrl(returnedNextLink.href))) {
        console.warn('Platin infinite scroll stopped a backwards/repeated pagination link:', returnedNextLink.href);
        this.paginationElement.innerHTML = '';
      }

      if (this.settings.callback) {
        this.settings.callback(responseDocument);
      }
    } catch (error) {
      if (!error || error.name !== 'AbortError') {
        console.warn('Platin infinite scroll load failed:', error);
      }

      // Allow a genuine transient network failure to be retried. The URL guard
      // is only retained for responses that reached the parsing stage.
      this.loadedUrls.delete(canonicalUrl);

      if (this.nextPageLinkElement && this.nextPageLinkElement.isConnected) {
        this.nextPageLinkElement.textContent = this.originalLoadingText || 'Loading More';
        this.nextPageLinkElement.removeAttribute('aria-busy');
      }
    } finally {
      this.loading = false;
      this.abortController = null;

      if (!this.destroyed && this.paginationElement && this.paginationElement.querySelector('a[href]')) {
        this.initialize();
      }
    }
  };

  Ajaxinate.prototype.removePaginationElement = function removePaginationElement() {
    if (this.paginationElement) {
      this.paginationElement.innerHTML = '';
    }
    this.destroy();
  };

  Ajaxinate.prototype.destroy = function destroy() {
    if (this.destroyed) return this;

    this.destroyed = true;
    this.removeScrollListener();
    this.removePaginationObserver();
    this.removeClickListener();

    if (this.abortController) {
      try {
        this.abortController.abort();
      } catch (error) {}
    }

    return this;
  };

  window.Ajaxinate = Ajaxinate;
})();
